import type { VaultReferenceContext, VaultReferenceProvider } from "@spuerhund/shared/vault";
import * as db from "./persistence";

const VAULT_VERSION = 1;
const VERIFY_TEXT = "spuerhund-vault-v1";
const PBKDF2_ITERATIONS = 250_000;

let unlockedKey: CryptoKey | null = null;

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function encodeUtf8(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function decodeUtf8(value: ArrayBuffer): string {
  return new TextDecoder().decode(value);
}

async function deriveVaultKey(passphrase: string, saltB64: string, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(encodeUtf8(passphrase)),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toArrayBuffer(fromBase64(saltB64)),
      iterations,
      hash: "SHA-256",
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function encryptString(value: string, key: CryptoKey): Promise<{ ciphertext: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    toArrayBuffer(encodeUtf8(value)),
  );

  return {
    ciphertext: toBase64(new Uint8Array(ciphertext)),
    iv: toBase64(iv),
  };
}

async function decryptString(ciphertextB64: string, ivB64: string, key: CryptoKey): Promise<string> {
  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(fromBase64(ivB64)) },
      key,
      toArrayBuffer(fromBase64(ciphertextB64)),
    );
  } catch (error) {
    if (error instanceof DOMException || (error as { name?: string } | null)?.name === "OperationError") {
      throw new Error(
        "Vault decryption failed (AES-GCM auth tag mismatch) — the entry was encrypted with a different passphrase or the vault key on this device is stale. Lock and re-unlock the vault, or re-enter the affected secret.",
      );
    }
    throw error;
  }

  return decodeUtf8(plaintext);
}

function requireUnlockedKey(): CryptoKey {
  if (!unlockedKey) {
    throw new Error("Vault is locked");
  }
  return unlockedKey;
}

export async function refreshVaultSnapshot() {
  const meta = await db.getVaultMeta();
  const entries = meta ? await db.listVaultEntries() : [];
  const isRemembered = meta ? await db.hasRememberedVaultKey() : false;
  return {
    meta,
    entries,
    hasVault: meta !== null,
    isUnlocked: unlockedKey !== null,
    isRemembered,
  };
}

export async function createVault(passphrase: string, options?: { rememberOnDevice?: boolean }) {
  const existing = await db.getVaultMeta();
  if (existing) {
    throw new Error("Vault already exists");
  }
  if (!passphrase.trim()) {
    throw new Error("Passphrase is required");
  }

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const saltB64 = toBase64(salt);
  const key = await deriveVaultKey(passphrase, saltB64, PBKDF2_ITERATIONS);
  const verification = await encryptString(VERIFY_TEXT, key);
  const now = new Date().toISOString();

  await db.setVaultMeta({
    id: "vault",
    version: VAULT_VERSION,
    salt: saltB64,
    iterations: PBKDF2_ITERATIONS,
    verificationCiphertext: verification.ciphertext,
    verificationIv: verification.iv,
    createdAt: now,
    updatedAt: now,
  });

  if (options?.rememberOnDevice) {
    await db.setRememberedVaultKey(key);
  } else {
    await db.clearRememberedVaultKey();
  }

  unlockedKey = key;
  return refreshVaultSnapshot();
}

export async function unlockVault(passphrase: string, options?: { rememberOnDevice?: boolean }) {
  const meta = await db.getVaultMeta();
  if (!meta) {
    throw new Error("Vault is not initialized");
  }
  if (!passphrase.trim()) {
    throw new Error("Passphrase is required");
  }

  const key = await deriveVaultKey(passphrase, meta.salt, meta.iterations);

  let verificationText: string;
  try {
    verificationText = await decryptString(meta.verificationCiphertext, meta.verificationIv, key);
  } catch {
    throw new Error("Incorrect passphrase");
  }

  if (verificationText !== VERIFY_TEXT) {
    throw new Error("Incorrect passphrase");
  }

  if (options?.rememberOnDevice) {
    await db.setRememberedVaultKey(key);
  } else {
    await db.clearRememberedVaultKey();
  }

  unlockedKey = key;
  return refreshVaultSnapshot();
}

export function lockVault() {
  unlockedKey = null;
}

/** Inject an externally-derived key used by the signed-in account vault. */
export function setUnlockedKey(key: CryptoKey) {
  unlockedKey = key;
}

export function isVaultUnlocked(): boolean {
  return unlockedKey !== null;
}

export async function restoreRememberedVault() {
  const meta = await db.getVaultMeta();
  if (!meta) {
    unlockedKey = null;
    return refreshVaultSnapshot();
  }

  const rememberedKey = await db.getRememberedVaultKey();
  if (!rememberedKey) {
    unlockedKey = null;
    return refreshVaultSnapshot();
  }

  try {
    const verificationText = await decryptString(meta.verificationCiphertext, meta.verificationIv, rememberedKey);
    if (verificationText !== VERIFY_TEXT) {
      throw new Error("Stored vault key verification failed");
    }
    unlockedKey = rememberedKey;
  } catch {
    unlockedKey = null;
    await db.clearRememberedVaultKey();
  }

  return refreshVaultSnapshot();
}

export async function forgetRememberedVault() {
  await db.clearRememberedVaultKey();
  return refreshVaultSnapshot();
}

export async function readVaultEntryValue(id: string): Promise<string> {
  const entry = await db.getVaultEntry(id);
  if (!entry) throw new Error("Vault entry not found");
  return decryptString(entry.ciphertext, entry.iv, requireUnlockedKey());
}

export async function saveVaultEntry(input: {
  id?: string;
  key: string;
  kind: db.VaultEntryKind;
  scopeType: db.VaultScopeType;
  scopeServerId?: string;
  description?: string;
  value: string;
}) {
  if (!input.key.trim()) {
    throw new Error("Key is required");
  }

  const encrypted = await encryptString(input.value, requireUnlockedKey());

  if (input.id) {
    await db.updateVaultEntry(input.id, {
      key: input.key.trim(),
      kind: input.kind,
      scopeType: input.scopeType,
      ...(input.scopeType === "server" && input.scopeServerId ? { scopeServerId: input.scopeServerId } : { scopeServerId: undefined }),
      description: input.description ?? "",
      ciphertext: encrypted.ciphertext,
      iv: encrypted.iv,
    });
  } else {
    await db.createVaultEntry({
      key: input.key.trim(),
      kind: input.kind,
      scopeType: input.scopeType,
      ...(input.scopeType === "server" && input.scopeServerId ? { scopeServerId: input.scopeServerId } : {}),
      description: input.description ?? "",
      ciphertext: encrypted.ciphertext,
      iv: encrypted.iv,
    });
  }

  return refreshVaultSnapshot();
}

export async function removeVaultEntry(id: string) {
  await db.deleteVaultEntry(id);
  return refreshVaultSnapshot();
}

export async function exportVaultData() {
  return db.exportVaultData();
}

export async function importVaultData(data: db.SpuerhundVaultExport) {
  await db.importVaultData(data);
  await db.clearRememberedVaultKey();
  lockVault();
  return refreshVaultSnapshot();
}

async function resolveVaultValue(
  kind: db.VaultEntryKind,
  key: string,
  context?: VaultReferenceContext,
): Promise<string> {
  const entries = await db.listVaultEntries();
  const serverScoped = context?.serverId
    ? entries.find((entry) =>
      entry.kind === kind
      && entry.key === key
      && entry.scopeType === "server"
      && entry.scopeServerId === context.serverId,
    )
    : undefined;
  const globalEntry = entries.find((entry) =>
    entry.kind === kind
    && entry.key === key
    && entry.scopeType === "global",
  );
  const entry = serverScoped ?? globalEntry;

  if (!entry) {
    throw new Error(`Missing ${kind}: ${key}`);
  }

  return decryptString(entry.ciphertext, entry.iv, requireUnlockedKey());
}

export const browserVaultProvider: VaultReferenceProvider = {
  resolveValue: (kind, key, context) => resolveVaultValue(kind as db.VaultEntryKind, key, context),
};
